import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { fmtEur } from "@/lib/quote";

export const QuoteItemsEditor = ({ q, setQ, save, catalog }) => {
  const [addId, setAddId] = useState("");
  const [presets, setPresets] = useState([]);

  useEffect(() => {
    api.get("/admin/cocktail-presets").then((r) => setPresets(r.data)).catch(() => {});
  }, []);

  const setItem = (idx, field, value) => {
    const items = q.items.map((it, i) => (i === idx ? { ...it, [field]: value } : it));
    setQ({ ...q, items });
  };

  const addProduct = () => {
    const p = catalog.find((c) => c.id === addId);
    if (!p) return;
    const guests = q.guests.adults + (q.guests.teens || 0) + q.guests.children;
    const items = [...q.items, { product_id: p.id, name: p.name, category: p.category, price: p.price, vat: p.vat, unit: p.unit, qty: p.unit === "persona" ? guests : 1, discount: 0 }];
    save({ items });
    setAddId("");
  };

  const savePreset = async () => {
    if (!q.cocktail_list) return toast.error("Scrivi prima la cocktail list");
    const name = window.prompt("Nome della lista preferita:", "Lista classica");
    if (!name) return;
    const r = await api.post("/admin/cocktail-presets", { name, list: q.cocktail_list });
    setPresets([...presets, r.data]);
    toast.success("Lista salvata nei preferiti");
  };

  return (
    <div>
      <h3 className="text-xs font-bold uppercase tracking-widest text-[#C85A32] mb-1">Voci preventivo</h3>
      <div className="text-[10px] text-slate-500 mb-2">Colonne: <b>Quantità</b> · <b>Prezzo unitario €</b> · <b>Sconto %</b></div>
      <div className="space-y-2">
        {q.items.map((it, i) => (
          <div key={`${it.product_id}-${i}`} className="bg-[#1E293B] rounded-lg p-3 flex items-center gap-2 flex-wrap">
            <div className="flex-1 min-w-[140px] text-sm font-medium">{it.name}</div>
            <Input data-testid={`item-qty-${i}`} type="number" className="w-16 h-8 bg-[#0F172A] border-slate-700 text-white" value={it.qty} onChange={(e) => setItem(i, "qty", Number(e.target.value))} />
            <Input data-testid={`item-price-${i}`} type="number" step="0.5" className="w-20 h-8 bg-[#0F172A] border-slate-700 text-white" value={it.price} onChange={(e) => setItem(i, "price", Number(e.target.value))} />
            <Input data-testid={`item-discount-${i}`} type="number" placeholder="%" className="w-14 h-8 bg-[#0F172A] border-slate-700 text-white" value={it.discount || 0} onChange={(e) => setItem(i, "discount", Number(e.target.value))} />
            <button data-testid={`item-delete-${i}`} onClick={() => save({ items: q.items.filter((_, j) => j !== i) })} className="text-slate-500 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mt-2">
        <select data-testid="add-product-select" value={addId} onChange={(e) => setAddId(e.target.value)} className="flex-1 h-9 rounded-md bg-[#1E293B] border border-slate-700 text-sm px-2">
          <option value="">Aggiungi voce dal listino…</option>
          {catalog.map((p) => <option key={p.id} value={p.id}>{`${p.name} — ${fmtEur(p.price)}`}</option>)}
        </select>
        <Button data-testid="add-product-button" size="sm" onClick={addProduct} disabled={!addId} className="bg-[#C85A32] hover:bg-[#a94a28]"><Plus className="w-4 h-4" /></Button>
      </div>
      <div className="flex items-center gap-3 mt-3 flex-wrap">
        <label className="text-xs text-slate-400">Sconto extra %</label>
        <Input data-testid="admin-discount-input" type="number" className="w-20 h-8 bg-[#1E293B] border-slate-700 text-white" value={q.admin_discount || 0} onChange={(e) => setQ({ ...q, admin_discount: Number(e.target.value) })} />
        <Button data-testid="save-items-button" size="sm" variant="outline" className="border-slate-600 text-slate-900" onClick={() => save({ items: q.items, admin_discount: q.admin_discount })}>Salva modifiche</Button>
        <span className="font-mono-num font-bold text-[#F0B429] ml-auto text-lg">{fmtEur(q.totals.total)}</span>
      </div>
      <Textarea data-testid="admin-notes-input" placeholder="Note interne / note per il PDF…" className="mt-3 bg-[#1E293B] border-slate-700 text-white" value={q.admin_notes || ""} onChange={(e) => setQ({ ...q, admin_notes: e.target.value })} onBlur={() => save({ admin_notes: q.admin_notes })} />
      {(q.items.some((it) => it.name.toLowerCase().includes("open bar")) || q.cocktail_list) && (
        <div className="mt-3">
          <div className="text-xs font-bold uppercase tracking-widest text-[#C85A32] mb-1">🍸 Cocktail list della serata</div>
          <Textarea data-testid="cocktail-list-input" placeholder="Es. Spritz, Negroni, Mojito, Gin Tonic (max 4 per Standard, 8 per Premium)…" className="bg-[#1E293B] border-slate-700 text-white" value={q.cocktail_list || ""} onChange={(e) => setQ({ ...q, cocktail_list: e.target.value })} onBlur={() => save({ cocktail_list: q.cocktail_list })} />
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {presets.map((p) => (
              <span key={p.id} data-testid={`cocktail-preset-${p.id}`} className="flex items-center gap-1 text-xs border border-slate-600 rounded-full pl-2.5 pr-1 py-0.5">
                <button data-testid={`apply-preset-${p.id}`} title={p.list} onClick={() => save({ cocktail_list: p.list })} className="hover:text-[#F0B429]">{p.name}</button>
                <button data-testid={`delete-preset-${p.id}`} onClick={async () => { await api.delete(`/admin/cocktail-presets/${p.id}`); setPresets(presets.filter((x) => x.id !== p.id)); }} className="text-slate-500 hover:text-rose-400 px-1">×</button>
              </span>
            ))}
            <button data-testid="save-cocktail-preset" onClick={savePreset} className="text-xs text-[#F0B429] hover:underline">💾 Salva come preferita</button>
          </div>
          <div className="text-[10px] text-slate-500 mt-1">Cambia da evento a evento: finisce nel PDF del preventivo e nella scheda sala/bar</div>
        </div>
      )}
    </div>
  );
};
